import { type Channel, type Partner, partners } from "@expert/shared-types";

const IS_DEV = import.meta.env.VITE_ENV_TYPE === "development";
const CLOUD_SPECIFIC_SERVICE_PROVIDER = import.meta.env.VITE_CLOUD_SPECIFIC_SERVICE_PROVIDER;

export const SERVICE_PROVIDER_QS_KEY = "serviceProvider";
export const ALLOWED_PARTNER_QS_KEY = "allowedPartner";

export const serviceProviders = Object.freeze(["AMAZON_CONNECT", "TWILIO", "EXPERT_UNIVERSITY"] as const);

export type ServiceProvider = (typeof serviceProviders)[number];

export function isServiceProvider(value: unknown): value is ServiceProvider {
    return typeof value === "string" && (serviceProviders as readonly string[]).includes(value);
}

export function isAllowedPartner(value: unknown): value is Partner {
    return typeof value === "string" && (["No partners", ...partners] as readonly string[]).includes(value);
}

export function assertServiceProvider(value: unknown): asserts value is ServiceProvider {
    if (isServiceProvider(value)) return;
    throw new Error(`value is not a ServiceProvider: ${value?.toString()}`);
}

export function assertAllowedPartner(value: unknown): asserts value is Partner {
    if (isAllowedPartner(value)) return;
    throw new Error(`value is not an allowedPartner: ${value?.toString()}`);
}

export function getServiceProviderOverrideFromQueryParams() {
    if (!IS_DEV) return;
    const value = new URLSearchParams(window.location.search).get(SERVICE_PROVIDER_QS_KEY);
    if (value === null) return;
    assertServiceProvider(value);
    return value;
}

export function getAllowedPartnersOverride() {
    if (!IS_DEV) return;
    const values = new URLSearchParams(window.location.search).getAll(ALLOWED_PARTNER_QS_KEY);
    if (values.length === 0) return;
    for (const value of values) assertAllowedPartner(value);
    return values as Partner[];
}

/**
 * Local default service provider for a channel, used only when no ServiceProviderOverride flag
 * value is present. Chat and voice both default to Amazon Connect; any remaining Twilio cohort is
 * carved out by employeeId in the ServiceProviderOverride flag config, not here.
 *
 * `_partner` is retained for a possible future return of partner-based local rules and is
 * intentionally unused today.
 */
export function getServiceProvider(_partner: Partner | undefined, channel: Channel): ServiceProvider {
    // serviceProviderOverrideQueryParams should already be undefined in Prod,
    // but leaving the IS_DEV check here for extra safety
    const serviceProviderOverrideQueryParams = getServiceProviderOverrideFromQueryParams();
    if (IS_DEV && serviceProviderOverrideQueryParams) {
        return serviceProviderOverrideQueryParams;
    }

    if (channel === "chat") {
        // Cloud-specific dev/preview builds pin the provider explicitly.
        if (CLOUD_SPECIFIC_SERVICE_PROVIDER) {
            return CLOUD_SPECIFIC_SERVICE_PROVIDER as ServiceProvider;
        }
        // Chat defaults to Amazon Connect. The small remaining Twilio cohort is carved out by
        // employeeId in the ServiceProviderOverride flag config, not by partner here.
        return "AMAZON_CONNECT";
    }

    // Voice is 100% on Amazon Connect.
    return "AMAZON_CONNECT";
}

export function selectPartnerWithOverride(partner: Partner): Partner {
    const partnerOverride = getAllowedPartnersOverride();
    return partnerOverride ? partnerOverride[0] : partner;
}
